import { useState } from 'react'
import { Link } from 'react-router-dom'
import { getDownloadUrl } from '../../hooks/useSupplierSpecialReports'
import { CopyTableButtons } from './CopyTableButtons'
import { DB_TABLE_VISIBLE_CAP, type DbTableResult } from './dbTableAnswers'

type Props = {
  table: DbTableResult
  onAsk?: (question: string) => void
  askDisabled?: boolean
}

async function downloadStoredFile(filePath: string, fileName: string): Promise<void> {
  const signed = await getDownloadUrl(filePath)
  if (!signed) throw new Error('Could not download this report.')
  const response = await fetch(signed)
  if (!response.ok) throw new Error('Could not download this report.')
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = fileName || 'report'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(objectUrl)
}

/** Scrollable DB-backed table for Risk Analyst with Copy / Print. */
export function DbTableChatCard({ table, onAsk, askDisabled }: Props) {
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const visibleRows = table.rows.slice(0, DB_TABLE_VISIBLE_CAP)
  const total = table.rows.length
  const truncated = total > DB_TABLE_VISIBLE_CAP

  const downloadFile = async (filePath: string, fileName: string) => {
    setDownloadError(null)
    try {
      await downloadStoredFile(filePath, fileName)
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Could not download this report.')
    }
  }

  const preamble = [
    table.title,
    `${total} row${total === 1 ? '' : 's'} · from platform data · ${table.sourceLabel}`,
  ]

  return (
    <div className="space-y-2.5 min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-es-deep leading-snug">{table.title}</h3>
          <p className="text-[11px] text-es-muted mt-0.5">
            {truncated
              ? `Showing ${visibleRows.length} of ${total} rows · from platform data`
              : `${total} row${total === 1 ? '' : 's'} · from platform data`}
            <span className="text-es-muted/80"> · {table.sourceLabel}</span>
          </p>
        </div>
        <CopyTableButtons
          payload={{ headers: table.headers, rows: table.rows, preamble }}
        />
      </div>

      <div className="overflow-x-auto max-h-[min(48vh,22rem)] overflow-y-auto rounded-[var(--radius-md)] border border-es-soft bg-es-white">
        <table className="w-full text-left text-[11px] border-collapse min-w-[28rem]">
          <thead className="sticky top-0 bg-es-haze z-[1]">
            <tr>
              {table.headers.map((h) => (
                <th
                  key={h}
                  className="px-2 py-1.5 font-semibold uppercase tracking-wider text-es-muted border-b border-es-soft whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td
                  colSpan={table.headers.length}
                  className="px-2 py-4 text-es-muted text-center"
                >
                  Nothing in platform data matched this search.
                </td>
              </tr>
            ) : (
              visibleRows.map((row, i) => (
                <tr key={i} className="border-b border-es-soft/60 last:border-0 hover:bg-es-haze/40">
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className={`px-2 py-1.5 text-es-body align-top ${
                        j === 0 ? 'font-medium max-w-[14rem]' : 'max-w-[12rem]'
                      }`}
                    >
                      {j === 0 && table.rowHrefs?.[i] ? (
                        <Link to={table.rowHrefs[i] as string} className="line-clamp-2 break-words text-es-signal hover:underline">
                          {cell}
                        </Link>
                      ) : j === 0 && table.rowAsks?.[i] && onAsk ? (
                        <button
                          type="button"
                          disabled={askDisabled}
                          onClick={() => onAsk(table.rowAsks?.[i] as string)}
                          className="line-clamp-2 break-words text-left text-es-signal hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {cell}
                        </button>
                      ) : table.downloadColumn === j && table.downloadPaths?.[i] ? (
                        <button
                          type="button"
                          disabled={askDisabled}
                          onClick={() =>
                            downloadFile(
                              table.downloadPaths?.[i] as string,
                              table.downloadNames?.[i] || cell
                            )
                          }
                          className="line-clamp-2 break-words text-left text-es-signal hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {cell}
                        </button>
                      ) : (
                        <span className="line-clamp-2 break-words">{cell}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {downloadError && (
        <p className="text-xs text-red-600" role="alert">
          {downloadError}
        </p>
      )}
    </div>
  )
}
